// Static TDC boilerplate for the proposal PDF, genericized from the PR-011
// sales deck. Dynamic sections (selections, video mix, investment) come from
// StrategyData; everything here is brand-agnostic.

export const DECK = {
    company: "THE DESIRE COMPANY",
    docTitle: "VIDEO STRATEGY",

    whoWeAre: {
        eyebrow: "WHO WE ARE",
        headline: "TRUST INFRASTRUCTURE FOR RETAIL.",
        body:
            "Credentialed experts in. Commerce performance out. We help brands and retailers win at the digital shelf on credibility, not hype.",
        scene:
            "A shopper lands on your product page ready to buy — and pauses. The question they are asking, consciously or not: does this person actually know what they are talking about, or is this just brand marketing dressed up as advice? Expert-led video answers that question at the moment it costs you the sale.",
    },

    // Rendered as text wordmarks (no logo image assets in the portal —
    // swap to Image tiles if/when partner logo PNGs land in src/assets).
    brandPartners: ["S&T", "BOSE", "HP", "KitchenAid", "Pinterest", "Ray-Ban"],

    trustStats: [
        {
            stat: "2,300+",
            label: "credentialed expert professionals on the TDC platform",
        },
        {
            stat: "4×",
            label:
                "sales lift from TDC content on the Walmart corporate site — one of the lower results measured",
        },
        {
            stat: "40+",
            label:
                "brand partners across consumer tech, beauty, food, home, and apparel",
        },
    ],

    proof: {
        eyebrow: "THE PROOF",
        headline: "WHEN SHOPPERS SEE A CREDENTIALED EXPERT, THEY CONVERT",
        sub: "Shoppers arrive over-researched and under-reassured. These numbers explain why expert content closes the gap brand content cannot.",
        stats: [
            {
                stat: "38%",
                label: "of shoppers prefer content from an expert over content from the brand itself",
                source: "Wakefield Research 2022",
            },
            {
                stat: "83%",
                label: "of shoppers prefer content from an expert over content from another user",
                source: "Wakefield Research 2022",
            },
            {
                stat: "300%",
                label: "purchase confidence lift when shoppers engage with credentialed expert content on the PDP",
                source: "TDC platform data",
            },
        ],
    },

    retailProof: {
        eyebrow: "PROOF AT RETAIL",
        headline: "PROVEN AT THE RETAILERS WHERE YOUR BRAND COMPETES",
        stats: [
            {
                stat: "25%",
                label: "more spent per transaction when shoppers engage with expert content on the PDP",
            },
            {
                stat: "3×",
                label: "conversion lift on focus SKUs with credentialed expert content versus brand-only content",
            },
            {
                stat: "62%",
                label: "longer time spent on PDP when expert video content is present",
            },
        ],
    },

    agentic: {
        title: "AGENTIC COMMERCE — INCLUDED",
        body:
            "Every video ships AI-ready: auto-transcribed with agentic commerce code generated. Expert-delivered structured claims become the semantic layer AI shopping assistants surface when a shopper asks what to buy — your products become the answer the assistant recommends.",
    },

    process: {
        eyebrow: "PROCESS",
        headline: "FROM BRIEF TO LIVE ASSET IN 4–5 WEEKS — TDC OWNS EVERY STEP",
        tagline: "One expert. One shoot. Every surface.",
        steps: [
            {
                title: "Brief and casting",
                body: "TDC matches credentialed experts to your product claims and shopper objection map.",
            },
            {
                title: "Pre-production",
                body: "TDC scripts expert talking points and FAQ responses aligned to your brand standards and FTC guidelines.",
            },
            {
                title: "Shoot",
                body: "Single production day per expert. Every format captured in one cycle — near-zero production overhead for your team.",
            },
            {
                title: "Edit and QA",
                body: "TDC edits all formats, runs FTC compliance review, and delivers channel-ready assets for every surface you sell on.",
            },
            {
                title: "Deliver and measure",
                body: "Assets go live across your selected placements. TDC tracks conversion and engagement across the measurement window.",
            },
        ],
    },

    caseStudy: {
        eyebrow: "CASE STUDY",
        headline: "THE SAME TRUST PLAYBOOK, PROVEN ON A PREMIUM CPG BRAND",
        brandLine: "TRIPLE PASTE  |  PREMIUM CPG, INGREDIENT-TRUST, CLINICAL CLAIMS",
        challenge:
            "Standing out during Amazon Prime Day for a premium-priced product against lower-cost alternatives, when the core objection was 'why should I trust this formula over a cheaper option.'",
        solution:
            "A credentialed expert explained the formula on camera and modeled real-life use, providing the credibility the brand-produced content could not.",
        resultStat: "15-point",
        resultLabel: "CONVERSION RATE INCREASE DURING PRIME DAY",
        footer: "Same trust playbook. Same credentialed-voice solution.",
    },

    nextSteps: {
        eyebrow: "NEXT STEPS",
        headline: "THE STRATEGY THAT TELLS YOU WHETHER EXPERT CONTENT IS YOUR CONVERSION LEVER",
        bullets: [
            "Approve the strategy scope to lock a production window; TDC schedules the casting and brief call within 5 business days.",
            "No internal production resources required from your team — TDC owns casting, scripting, shoot, edit, and compliance.",
            "Assets go live within 4–5 weeks of approval; a clean measurement window gives you a conversion read before any broader commitment.",
        ],
        closer:
            "This strategy is how you find out whether your shopper actually needed more content — or someone worth listening to.",
    },

    disclaimer:
        "Pricing shown is an estimate based on your selections; the final quote is confirmed by the TDC team. There is no obligation at this stage.",
};

// Channel blurbs for the Where It Lives table, keyed by generic channel name.
export const CHANNEL_BLURBS: Record<string, string> = {
    PDP: "Product detail pages — yours, Amazon's, and retailer sites — educating and converting at the decision moment.",
    Sponsored: "Retail media and paid placements driving ROAS and capturing high-intent shoppers.",
    CTV: "Connected TV — expert content that builds awareness and trust on the big screen.",
    "Offsite OLV": "Email, retailer homepages, AI shopping experiences, and other offsite placements.",
    Offsite: "Email, retailer homepages, AI shopping experiences, and other offsite placements.",
    Social: "Meta, TikTok, YouTube, and more — stopping the scroll and driving action.",
    "In-Store": "In-store screens and displays that influence purchase decisions at the shelf.",
};

// RMN white-label overrides for the same table — a retailer's proposal is
// always for its own PDP, so the generic "yours, Amazon's, and retailer
// sites" framing doesn't apply (mirrors RMN_PDP_DESCRIPTION on the wizard's
// channel card). Channels not listed here fall back to CHANNEL_BLURBS.
export const RMN_CHANNEL_BLURBS: Record<string, string> = {
    PDP: "Retailer product detail pages — educating and converting at the decision moment.",
};

export const channelBlurb = (name: string, rmn?: boolean): string =>
    (rmn && RMN_CHANNEL_BLURBS[name]) ||
    CHANNEL_BLURBS[name] ||
    "Expert content tailored to this placement.";
